/**
 * Main-thread wrapper around the pitch-detection worklet.
 *
 * Lifecycle:
 *   init()    → loads the worklet module, builds the analyser node graph
 *   start()   → requests mic, attaches the stream, kicks off detection
 *   onPitch() → subscribe; multiple handlers allowed
 *   stop()    → tears the stream + node graph down (re-startable)
 *   destroy() → releases everything; the instance is single-use after this
 *
 * The audio graph:
 *
 *   MediaStreamAudioSourceNode → AudioWorkletNode → (no destination)
 *
 * The worklet acts as a sink; we deliberately do NOT route the worklet
 * output to `audioContext.destination` — that would echo the user's mic
 * back through their speakers and create feedback. The worklet posts
 * pitch frames to the main thread instead.
 */

import type { PitchWorkletMessage } from "./pitch-worklet-types.js";

export interface PitchEvent {
  /** Continuous MIDI value, e.g. 64.3 = sharp E4. */
  midi: number;
  /** Cents deviation from the nearest integer semitone, -50..+50. */
  cents: number;
  /** YIN/MPM clarity, 0..1. */
  clarity: number;
  /** AudioContext.currentTime when the analysis frame ended. */
  timestamp: number;
}

export type PitchDetectorState =
  | "idle"
  | "ready"
  | "starting"
  | "running"
  | "denied"
  | "error";

export interface PitchDetectorInitOptions {
  /**
   * URL to load the bundled pitch-detector worklet from. Required —
   * see `pitch-worklet-url.ts` for the recommended Vite consumer
   * pattern (`import workletUrl from "@music-app/input-mic/pitch-worklet?worker&url"`).
   * Tests can pass any string; the fake AudioContext doesn't actually
   * fetch the URL.
   */
  workletUrl: string;
}

export interface PitchDetector {
  init(
    audioContext: AudioContext,
    opts: PitchDetectorInitOptions,
  ): Promise<void>;
  start(): Promise<MediaStreamAudioSourceNode>;
  stop(): void;
  onPitch(handler: (event: PitchEvent) => void): () => void;
  destroy(): void;
  readonly state: PitchDetectorState;
}

interface PitchDetectorInternal extends PitchDetector {
  /* exposed for tests so we can inject a fake getUserMedia. The
   * production path goes through `navigator.mediaDevices.getUserMedia`
   * directly. */
  _setMediaProvider(provider: MediaProvider): void;
}

/** Indirection over `navigator.mediaDevices.getUserMedia` for tests. */
export type MediaProvider = (
  constraints: MediaStreamConstraints,
) => Promise<MediaStream>;

/**
 * Audio constraints — pitch detection is wrecked by browser-level audio
 * processing, so we explicitly disable echo cancellation, noise
 * suppression, and AGC. This is the single most common source of
 * "why is the detector seeing 70 Hz when I sang A4" bug reports across
 * pitch-detection projects.
 */
const PITCH_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

class PitchDetectorImpl implements PitchDetectorInternal {
  state: PitchDetectorState = "idle";

  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private mediaStream: MediaStream | null = null;
  private handlers: Set<(event: PitchEvent) => void> = new Set();
  private mediaProvider: MediaProvider;
  /** Promise from a previous start() call. Re-entrant start() returns
   *  the existing promise rather than racing two getUserMedia prompts. */
  private startPromise: Promise<MediaStreamAudioSourceNode> | null = null;

  constructor() {
    this.mediaProvider = (constraints) => {
      // Keep the lookup lazy — `navigator` is undefined in Node, and
      // tests never reach this branch because they install their own
      // provider via `_setMediaProvider`.
      const md = (globalThis as { navigator?: Navigator }).navigator
        ?.mediaDevices;
      if (!md) {
        return Promise.reject(
          new Error("navigator.mediaDevices is unavailable"),
        );
      }
      return md.getUserMedia(constraints);
    };
  }

  _setMediaProvider(provider: MediaProvider): void {
    this.mediaProvider = provider;
  }

  async init(
    audioContext: AudioContext,
    opts: PitchDetectorInitOptions,
  ): Promise<void> {
    if (this.audioContext) {
      // Idempotent — repeated init() calls (e.g. on a re-mount) reuse
      // the same context and don't re-load the worklet module.
      return;
    }
    this.audioContext = audioContext;
    try {
      await audioContext.audioWorklet.addModule(opts.workletUrl);
      this.state = "ready";
    } catch (err) {
      this.state = "error";
      throw err;
    }
  }

  start(): Promise<MediaStreamAudioSourceNode> {
    // Re-entrant: if start() is already in flight, share its promise.
    // The second caller does not get its own permission prompt.
    if (this.startPromise) return this.startPromise;
    if (this.sourceNode) return Promise.resolve(this.sourceNode);
    if (this.state === "denied") {
      // Don't re-prompt automatically. The caller has to destroy() first
      // (or reload). This avoids spamming the permission UI.
      return Promise.reject(new Error("Microphone permission was denied"));
    }
    if (!this.audioContext) {
      return Promise.reject(new Error("PitchDetector.init() not called"));
    }

    this.state = "starting";
    const audioContext = this.audioContext;

    this.startPromise = (async () => {
      let stream: MediaStream;
      try {
        stream = await this.mediaProvider({ audio: PITCH_AUDIO_CONSTRAINTS });
      } catch (err) {
        const name = (err as { name?: string }).name;
        // NotAllowedError = user clicked "Block". SecurityError = origin
        // not eligible (e.g. http:// in production). Both should land us
        // in `denied`; everything else is a transient error.
        if (name === "NotAllowedError" || name === "SecurityError") {
          this.state = "denied";
        } else {
          this.state = "error";
        }
        throw err;
      }
      this.mediaStream = stream;

      const source = audioContext.createMediaStreamSource(stream);
      this.sourceNode = source;

      const node = new AudioWorkletNode(audioContext, "pitch-detector");
      node.port.onmessage = (event: MessageEvent<PitchWorkletMessage>) => {
        const m = event.data;
        // Defensive validation: a misbehaving worklet that posted a
        // garbled message shouldn't crash the page.
        if (
          !m ||
          typeof m.midi !== "number" ||
          typeof m.cents !== "number" ||
          typeof m.clarity !== "number" ||
          typeof m.timestamp !== "number"
        ) {
          return;
        }
        const pitchEvent: PitchEvent = {
          midi: m.midi,
          cents: m.cents,
          clarity: m.clarity,
          timestamp: m.timestamp,
        };
        for (const handler of this.handlers) handler(pitchEvent);
      };
      this.workletNode = node;
      source.connect(node);
      // Intentionally NOT connecting `node` to `audioContext.destination`
      // — see file header.
      this.state = "running";
      return source;
    })();

    // `.finally(...)` returns a NEW promise that mirrors the original's
    // settlement. If `startPromise` rejects, that new promise rejects too —
    // and unless something handles it, Node logs an unhandled rejection.
    // The original `startPromise` is returned to the caller (which DOES
    // handle it), so the second handle just needs a no-op `.catch()`.
    this.startPromise
      .finally(() => {
        this.startPromise = null;
      })
      .catch(() => {});

    return this.startPromise;
  }

  stop(): void {
    // Tear down the stream + node graph but keep `audioContext` and the
    // loaded worklet module so a subsequent start() is fast (no second
    // addModule round-trip).
    if (this.mediaStream) {
      for (const track of this.mediaStream.getTracks()) track.stop();
      this.mediaStream = null;
    }
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch {
        // disconnect() throws if already disconnected — fine.
      }
      this.sourceNode = null;
    }
    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      try {
        this.workletNode.disconnect();
      } catch {
        // ditto
      }
      this.workletNode = null;
    }
    if (this.state !== "denied" && this.state !== "error") {
      this.state = this.audioContext ? "ready" : "idle";
    }
  }

  onPitch(handler: (event: PitchEvent) => void): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  destroy(): void {
    this.stop();
    this.handlers.clear();
    this.audioContext = null;
    this.state = "idle";
  }
}

export function createPitchDetector(): PitchDetector {
  return new PitchDetectorImpl();
}

/**
 * For tests: create a detector with a stubbed media provider so we don't
 * have to touch the real navigator.mediaDevices API. Equivalent to
 * `createPitchDetector()` followed by `_setMediaProvider`.
 */
export function createPitchDetectorWithMediaProvider(
  provider: MediaProvider,
): PitchDetector {
  const detector = new PitchDetectorImpl();
  detector._setMediaProvider(provider);
  return detector;
}
